import React, { MouseEvent, useRef } from 'react';
import { buildClassName } from 'utils/build-class-name';
import { useModalContext, useSavedListingContext, useThemeContext, useUserContext } from 'contexts';
import styles from './style.module.scss';
import { dynamicTestIds, listingCardIds } from 'constants/test-constants';
import HollowHeartIconV2 from 'components/icon/hollow-heart-icon-v2';
import { AnalyticsController } from 'analytics/analytics-controller';
import { isExpTenant } from '@tenants';

type FavouriteToggle = {
  listingId: number;
  isInCarousel?: boolean;
  isSmallCard?: boolean;
  isModal?: boolean;
  isInline?: boolean;
  addressSlug?: string;
};

type FavouriteToggleBaseProps = {
  toggleFavouriteState: () => void;
  isFavourite: boolean;
  isInCarousel?: boolean;
  isSmallCard?: boolean;
  isInline?: boolean;
  isLoading?: boolean;
};

export const FavouriteToggleBase = ({ isInCarousel, isSmallCard, isFavourite, toggleFavouriteState, isInline, isLoading }: FavouriteToggleBaseProps) => {
  const containerClassName = buildClassName(isInCarousel && styles.component, isSmallCard && styles['small-card'], styles['favourite-wrapper'], isInCarousel && styles['address-page-icon'], isInline && styles['inline']);
  const dynamicTestid = isFavourite ? dynamicTestIds.favourite : dynamicTestIds.notFavourite;
  const hasBeenClicked = useRef(false);
  const handleOnCLick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    toggleFavouriteState?.();
    if (!hasBeenClicked.current) {
      AnalyticsController.trackEvent('XpB_ListingMHeartButClk');
      hasBeenClicked.current = true;
    }
  };
  const { tenantId } = useThemeContext();
  const useExpTheme = isExpTenant(tenantId);

  return (
    <div className={containerClassName} onClick={handleOnCLick} data-testid={listingCardIds.favouriteToggle} data-dynamic-testid={dynamicTestid}>
      { isLoading ? (
        <div className={styles.spinnerWrapper}>
          <div className={styles.spinner} />
        </div>
      ) : (
        <HollowHeartIconV2
          className={buildClassName(styles.favourite, isFavourite && styles.active, useExpTheme && styles.exp)}
          onClick={handleOnCLick}
        />
      )}
    </div>
  );
};

export default function FavouriteToggleV2({ listingId, addressSlug, isInCarousel, isSmallCard, isModal = false, isInline = false }: FavouriteToggle) {
  const { openModal } = useModalContext();

  const { isAuthenticated } = useUserContext();
  const { addSavedListing, removeSavedListing, isSavedListing } = useSavedListingContext();

  const isFavourite = isSavedListing(addressSlug, listingId);

  const toggleFavouriteState = () => {
    if (isAuthenticated) {
      if (isFavourite) {
        removeSavedListing(listingId);
      } else {
        addSavedListing({ listingId, isModal });
      }
    } else {
      openModal('login-registration');
    }
  };

  return (
    <FavouriteToggleBase
      isFavourite={isFavourite}
      isInCarousel={isInCarousel}
      isSmallCard={isSmallCard}
      toggleFavouriteState={toggleFavouriteState}
      isInline={isInline}
    />
  );
}
